import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SourcesLink } from "../components/SourcesLink";
import { DataStamp } from "../components/DataLabel";
import { ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";

const usd = (n) => (n >= 1e6 ? `$${(n / 1e6).toFixed(2)}m` : `$${Math.round(n || 0).toLocaleString()}`);
const int = (n) => Math.round(n || 0).toLocaleString();

const Stat = ({ label, value, note, testId }) => (
  <div className="bg-[#0b0b0b] p-6" data-testid={testId}>
    <p className="overline !text-zinc-500">{label}</p>
    <p className="mono mt-3 text-2xl font-bold text-ochre md:text-3xl">{value}</p>
    {note ? <p className="mt-2 text-[0.68rem] uppercase tracking-[0.14em] text-zinc-400">{note}</p> : null}
  </div>
);

export default function Trading() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: "Player Trading Model",
    description:
      "What one produced Kenyan professional returns: first transfer fee, sell-on share, FIFA training compensation and solidarity contributions, career earnings and the share that comes home — county by county.",
  });

  useEffect(() => {
    api.get("/trading-model").then((r) => setD(r.data)).catch(() => setErr("We could not load the trading model. Refresh to try again."));
  }, []);

  if (err)
    return (
      <div className="bg-black px-5 pb-24 pt-24 md:px-10" data-testid="trading-error">
        <p className="border border-maasai/40 bg-maasai/10 p-6 text-sm text-white">{err}</p>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="trading-loading" />;
  const p = d.per_professional;
  const n = d.national;

  return (
    <div className="bg-black pb-24 pt-12 md:pt-16" data-testid="trading-page">
      <section className="px-5 md:px-10">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <p className="eyebrow">Player trading model</p>
            <h1 className="display-1 mt-5 max-w-4xl">What One Professional Returns</h1>
            <p className="body-copy mt-6 max-w-3xl">
              A produced professional is not a cost that ends at eighteen. Every graduate lands somewhere — a domestic
              contract, a regional or second-tier move abroad, a mid-tier European or Gulf top division, or the
              top-five league outlier that pays for everything else. Each destination carries a first fee, a likely
              onward sale, and a career. Two streams matter and they are never mixed here: the trading income that
              returns to the academy and the county, and the player&rsquo;s own earnings, part of which comes home.
            </p>
          </Reveal>

          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/phasing" className="btn-primary" data-testid="trading-phasing-link">
              How it funds the build <ArrowUpRight size={14} />
            </Link>
            <Link to="/facilities" className="btn-ghost" data-testid="trading-facilities-link">
              Cost to produce a professional
            </Link>
          </div>

          <div className="mt-12 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="trading-headline">
            <Stat label="Cost to produce one" value={usd(p.cost_usd)} note="Average across the counties" testId="trading-cost" />
            <Stat label="Trading income per professional" value={usd(p.programme_income_usd)} note={`${p.return_multiple}x the cost of production`} testId="trading-per-pro" />
            <Stat label="Career earnings per professional" value={usd(p.career_earnings_usd)} note={`${usd(p.remittance_usd)} comes home`} testId="trading-earnings" />
            <Stat label="Total economic value per professional" value={usd(p.total_economic_usd)} note="Trading plus career earnings" testId="trading-total" />
          </div>

          <div className="mt-10 overflow-x-auto border border-zinc-800" data-testid="trading-table">
            <table className="w-full min-w-[1080px] border-collapse text-left">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {["Destination", "Share of graduates", "First fee", "Onward sale", "Sell-on 15%", "Solidarity and training", "Trading income", "Career earnings"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.rows.map((r, i) => (
                  <tr key={r.tier} className="border-b border-zinc-900" data-testid={`trading-row-${i}`}>
                    <td className="px-4 py-3 text-sm text-white">
                      {r.tier}
                      <span className="mt-1 block max-w-md text-[0.7rem] leading-relaxed text-zinc-500">{r.note}</span>
                    </td>
                    <td className="mono px-4 py-3 text-sm text-ochre">{Math.round(r.share * 100)}%</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.first_fee_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.onward_fee_usd ? usd(r.onward_fee_usd) : "—"}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.sell_on_usd ? usd(r.sell_on_usd) : "—"}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.solidarity_and_training_usd ? usd(r.solidarity_and_training_usd) : "—"}</td>
                    <td className="mono px-4 py-3 text-sm text-ochre">{usd(r.programme_income_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.career_earnings_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <h2 className="display-2 mt-16 max-w-4xl">{n.pros_per_year} professionals a year, {usd(n.programme_income_usd)} back into Kenya</h2>
          <p className="body-copy mt-6 max-w-3xl" data-testid="trading-national-note">{n.note}</p>

          <div className="mt-10 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="trading-national">
            <Stat label="Professionals produced a year" value={int(n.pros_per_year)} note={`${d.assumptions.players_per_professional} academy places per professional`} testId="trading-pros" />
            <Stat label="Trading income a year" value={usd(n.programme_income_usd)} note={`${usd(n.ten_year_programme_usd)} over ten years`} testId="trading-national-income" />
            <Stat label="Player earnings a year" value={usd(n.player_earnings_usd)} note={`${usd(n.remittance_usd)} returning as remittance`} testId="trading-national-earnings" />
            <Stat label="Cost of producing them" value={usd(n.cost_usd)} note="Annual development cost of that cohort" testId="trading-national-cost" />
          </div>

          <h3 className="display-3 mt-16">County by county</h3>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-zinc-400">
            Only counties holding a county academy produce professionals. Return multiple is the trading income per professional against what that county spends to
            produce one — the counties at the top of this list are where a pound of development money works hardest.
          </p>
          <div className="mt-8 overflow-x-auto border border-zinc-800" data-testid="trading-county-table">
            <table className="w-full min-w-[980px] border-collapse text-left">
              <thead>
                <tr className="border-b border-zinc-800 bg-[#0b0b0b]">
                  {["County", "Academy places", "Professionals a year", "Cost per professional", "Trading income", "Player earnings", "Remittance home", "Return"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[0.65rem] font-bold uppercase tracking-[0.18em] text-zinc-300">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {d.counties.map((r, i) => (
                  <tr key={r.county} className="border-b border-zinc-900" data-testid={`trading-county-row-${i}`}>
                    <td className="px-4 py-3 text-sm text-white">{r.county}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{int(r.academy_places)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.pros_per_year}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.cost_per_professional_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-ochre">{usd(r.programme_income_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.player_earnings_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.remittance_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.return_multiple}x</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-10 grid gap-6 lg:grid-cols-2">
            <div className="border-l-2 border-ochre bg-[#0b0b0b] p-6" data-testid="trading-method">
              <p className="overline text-ochre">The method</p>
              <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-zinc-300">{d.method}</p>
            </div>
            <div className="border border-white/10 bg-[#0b0b0b] p-6" data-testid="trading-sources">
              <p className="overline">Sources</p>
              <SourcesLink testId="trading-sources-link" className="!mt-3" />
              <div className="mt-4 flex flex-col gap-3 text-xs text-zinc-300">
                {d.sources.map((s) => (
                  <div key={s.url + s.label}>
                    <a href={s.url} target="_blank" rel="noreferrer" className="underline underline-offset-4 hover:text-ochre">
                      {s.source}
                    </a>
                    <DataStamp source={s} className="!mt-2" testId={`trading-source-stamp-${s.verification}`} />
                  </div>
                ))}
              </div>
              <p className="mt-5 text-xs leading-relaxed text-zinc-400">
                Destination probabilities, fees and career lengths are our own estimates, benchmarked on documented
                Kenyan and East African moves rather than West African outliers, and published so they can be argued
                with.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
